import { useEffect, useState } from 'react'
import { type UpdateTrainingFormValues } from '../pages/pageComponents/planning/priorityActivities/PriorityOne/AddEditTrainingModal'
import { type RCNPD } from '../models/RCNPD'
import { GetRCNPDsForPriority, LockActivity, UnlockActivity } from '../services/AnnualPlanService'
import { PriorityOneActivityName } from '../models/PriorityOneActivityName'
import type { AddRCNPDSeriesSessionFormValues, RCNPDSeriesSession } from '../models/RCNPDSeriesSession'
import { handleApiResponse } from '../utils/apiHelpers'

interface UseTrainingsType {
  isLoading: boolean
  seriesTrainings: RCNPD[]
  singleTrainings: RCNPD[]
  reloadTrainings: () => Promise<void>
}
export const useTrainings = (priorityId: number, isImplementationPhase?: boolean): UseTrainingsType => {
  const [trainings, setTrainings] = useState<RCNPD[]>([])
  const [isLoading, setIsLoading] = useState(false)

  const loadTrainings = async (): Promise<void> => {
    setIsLoading(true)

    const trainings = await handleApiResponse(GetRCNPDsForPriority(priorityId, isImplementationPhase !== true))
    if (trainings != null) setTrainings(trainings)

    setIsLoading(false)
  }

  useEffect(() => {
    void loadTrainings()
  }, [])

  const seriesTrainings = trainings.filter(pd => pd.isSeries)
  const singleTrainings = trainings.filter(pd => !pd.isSeries)

  return {
    isLoading,
    seriesTrainings,
    singleTrainings,
    reloadTrainings: loadTrainings
  }
}

interface UseTrainingModalType {
  showTrainingModal: boolean
  initialValues: UpdateTrainingFormValues | undefined
  handleEditTraining: (training: RCNPD) => Promise<void>
  handleCloseModal: (isUpdate?: boolean) => Promise<void>
  handleAddTraining: () => void
  handleUpdateTraining: (training: RCNPD) => Promise<void>
}
export const useTrainingModal = (): UseTrainingModalType => {
  const [showTrainingModal, setShowTrainingModal] = useState(false)
  const [initialValues, setInitialValues] = useState<UpdateTrainingFormValues>()

  const handleEditTraining = async (training: RCNPD): Promise<void> => {
    const addSeriesSession = (session?: RCNPDSeriesSession): AddRCNPDSeriesSessionFormValues => ({
      id: session?.id ?? 0,
      rcnpdUpdateId: session?.rcnpdUpdateId ?? training.id,
      name: session?.name ?? '',
      contentFocus: session?.contentFocus ?? [],
      date: session?.date ?? null,
      length: session?.length ?? '',
      trainingHeld: session?.trainingHeld,
      numberOfAttendees: session?.numberOfAttendees ?? null,
      numberOfTeamsTrained: session?.numberOfTeamsTrained ?? null,
      budget: session?.budget ?? null,
      budgetSpent: session?.budgetSpent ?? null
    })
    let seriesSessions: undefined | AddRCNPDSeriesSessionFormValues[]
    if (training.isSeries) {
      if (training?.seriesSessions != null && Array.isArray(training.seriesSessions) && training.seriesSessions.length > 0) {
        seriesSessions = training.seriesSessions.map(addSeriesSession)
      }
      // Leave undefined when the training has no series sessions yet — the modal
      // decides whether to seed an empty row or carry over contract-phase sessions.
    }
    const initialValues: UpdateTrainingFormValues = {
      id: training.id,
      priorityId: training.priorityId,
      title: training.title,
      organizationIds: training.organizations.map(o => o.id),
      trainingFormat: training.trainingFormat,
      scope: training.scope,
      open: training.open,
      resourcesNeeded: training.resourcesNeeded,
      budgetRequest: String(training.budgetRequest),
      isSeries: training.isSeries,
      contentFocus: training.contentFocus,
      trainingDate: training.trainingDate,
      trainingLength: training.trainingLength,
      isUpdate: training.isUpdate,
      actualBudgetAmount: training.actualBudgetAmount != null ? String(training.actualBudgetAmount) : null,
      trainingHeld: training.trainingHeld,
      numberOfAttendees: training.numberOfAttendees != null ? String(training.numberOfAttendees) : null,
      numberOfTeamsTrained: training.numberOfTeamsTrained != null ? String(training.numberOfTeamsTrained) : null,
      seriesSessions,
      contractSessions: training.contractSessions?.map(cs => ({
        id: cs.id,
        name: cs.name,
        date: cs.date
      }))
    }
    setInitialValues(initialValues)
    setShowTrainingModal(true)
    await LockActivity(
      training.id,
      PriorityOneActivityName.RcnProvidedProfessionalDevelopment,
      training.isUpdate === true
    )
  }

  const handleCloseModal = async (isUpdate?: boolean): Promise<void> => {
    setShowTrainingModal(false)
    if (initialValues != null) {
      await UnlockActivity(
        initialValues.id,
        PriorityOneActivityName.RcnProvidedProfessionalDevelopment,
        isUpdate === true
      )
    }
  }

  const handleAddTraining = (): void => {
    setInitialValues(undefined)
    setShowTrainingModal(true)
  }

  const handleUpdateTraining = async (training: RCNPD): Promise<void> => {
    await handleEditTraining(training)
  }

  return {
    showTrainingModal,
    initialValues,
    handleEditTraining,
    handleCloseModal,
    handleAddTraining,
    handleUpdateTraining
  }
}
